import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Cost, DEAL_CONFIRMED, DealRow, Formula, MatchedOffer, TableKey } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { forEach, identity, isEqual, pick } from 'lodash-es'
import { Subject } from 'rxjs'
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators'
import { loadDealFilters, loadDealViews, setDestLocationSuccess, setOriginLocationSuccess, setSegmentDateSuccess } from 'src/app/store/deal-view.actions'
import { selectDealsFilters, selectDealsTable, selectDealsTotals } from 'src/app/store/deal-view.reducer'
import { ROUTES } from 'src/app/routes.constants'
import { AVAILABLE_COLUMNS, DEFAULT_COLUMNS, DealsListComponent, columnDefsById, columnNames } from 'src/components/deals-list'
import { DEFAULT_DEALS_FILTERS, TOP_LEVEL_FILTERS_COLUMNS } from 'src/components/deals-list/deals-list.filters'
import { DealFormPageOverlayService } from 'src/pages/admin/trading/deal-form/deal-form-page/deal-form-page-overlay/deal-form-page-overlay.component.service'
import { DealElasticSearchService } from 'src/services/data/deal-elastic.service'
import { FilterDataSelect, FilterDataSelectOption } from 'src/services/elastic-search'
import { FiltersService, TABLE_FILTERS_FIELDS, TableFilters } from 'src/services/table-utils'
import { ApiTableDataSource } from 'src/services/table-utils/data-sources/api-table-data-source'
import { replayForm } from 'src/shared/utils/replay-form'
import { StateService } from '@uirouter/angularjs'
import { ConfirmModalService } from 'src/components/confirm/confirm-modal.service'

const getSign = (v) => v >= 0 ? '+' : '-';
const getAbsolutePrice = (v) => Math.abs(v);

 
export interface InventoryDealsFormOptions {
  matchedOffer?: any
  product?: string
  price?: string | number
  deliveryDates?: {from: number; to: number}
  futureDates?: {from: number; to: number}
  isNotSubRow?: boolean
  future_bid_id?: string
  shipmentDates?: {from: number; to: number}
  [key: string]: unknown
}

@Component({
  selector: 'tc-inventory-deals',
  templateUrl: './inventory-deals-form.component.html',
  styleUrls: ['./inventory-deals-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class InventoryDealsFormComponent extends OnDestroyMixin implements OnInit {
  private elastic = inject(DealElasticSearchService)
  private dealFormPageOverlayService = inject(DealFormPageOverlayService)
  private Filters = inject(FiltersService)
  private store = inject(Store)
  private actions$ = inject(Actions)
  private dialogRef = inject<MatDialogRef<InventoryDealsFormComponent>>(MatDialogRef)
  data = inject<InventoryDealsFormOptions>(MAT_DIALOG_DATA)
  private router = inject(Router)
  private ConfirmModal = inject(ConfirmModalService)


  constructor() {
    const $state = inject<StateService>('$state' as any)

    super()
    this.$state = $state
  }

  protected readonly $state: StateService

  // page configuration
  tableIdentity = TableKey.DealInventory

  selectedDeals: DeepReadonly<DealRow[]> = []
  // table configurations
  readonly columnDefs = columnDefsById(this.tableIdentity)
  readonly availableColumns = AVAILABLE_COLUMNS[this.tableIdentity]
  readonly columnNames = columnNames(this.tableIdentity)
  price = ''

  filtersForm = this.Filters.buildForm(this, {
    key: this.tableIdentity,
    cacheFilters: false,
    defaultValues: {
      ...DEFAULT_DEALS_FILTERS,
      columns: DEFAULT_COLUMNS[this.tableIdentity],
      product: [this.data.product],
      status: [DEAL_CONFIRMED],
      deal_type: "bwi",
      convertible: true,
      ...Object.assign({}, this.data.extendedName || {}),
    },
  })
  // dropdown options (filters)
  supplier$ = new Subject<FilterDataSelectOption[]>()
  selling_trader$ = new Subject<FilterDataSelectOption[]>()

  displayColumns$ = replayForm(this.filtersForm.controls.columns)

  // data, ros and totals
  totals$ = this.store.select(selectDealsTotals(this.tableIdentity))
  filtersData$ = this.store.select(selectDealsFilters(this.tableIdentity))
  dataSource: ApiTableDataSource<DeepReadonly<DealRow>, TableFilters>

  @ViewChild(DealsListComponent)
  dealsList: DealsListComponent

  // NOTE: we don't apply backend filters "on change"
  private flushBackendFilter$ = new Subject<{ force: boolean }>()
  private nextLoadResetTable = false
  private filtersForm$ = replayForm(this.filtersForm)

  // data, rows and totals
  private deals$ = this.store.select(selectDealsTable(this.tableIdentity))

  ngOnInit() {
    this.price = String(this.data.price || '')
    const matchedOfferData = this.data.matchedOffer as MatchedOffer & {isFormulaOffer?: boolean; formula?: Formula}
    if(matchedOfferData?.isFormulaOffer) {
      this.price = `${matchedOfferData.formula?.price_source || ''} ${getSign(Number(this.data.price))} ${getAbsolutePrice(Number(this.data.price))}`
    }
    // if user is searching by deal_id - disable all other filters
    this.filtersForm$
      .pipe(
        map((form) => !!form.deal_id),
        distinctUntilChanged(),
        untilComponentDestroyed(this)
      )
      .subscribe((searchDealId) => {
        forEach(this.filtersForm.controls, (control, name) => {
          if (control === this.filtersForm.controls.deal_id) return
          if (TABLE_FILTERS_FIELDS.includes(name)) return
          if (name === 'table_view_id') return
          if (searchDealId) control.disable()
          else control.enable()
        })
      })

    // load "top" filters data
    this.elastic
      .fetchDealFilters({ columns: TOP_LEVEL_FILTERS_COLUMNS[this.tableIdentity] }, this.tableIdentity)
      .pipe(untilComponentDestroyed(this))
      .subscribe((filtersData: Dictionary<FilterDataSelect>) => {
        this.supplier$.next(filtersData.supplier.options)
        this.selling_trader$.next(filtersData.selling_trader.options)
      })

    // trigger data reload
    const trigger$ = this.flushBackendFilter$.pipe(
      withLatestFrom(this.filtersForm$),
      distinctUntilChanged(([, a], [{force} = {force: false}, b]) =>
        // skip not forced triggers if filter form is pristine
        !force && isEqual(a, b)),
      map(([, filters]) => filters))

    // setup data source
    this.dataSource = new ApiTableDataSource<DeepReadonly<DealRow>, TableFilters>(
      this.deals$.pipe(filter(identity)),
      this.filtersForm,
      trigger$
    )

    // listen for fetch commands - fetch data
    // WA-22049: formula future bids still have to be matched with formula inventory deals,
    // but flat future bids can be assigned to any inventory deal - flat or formula.
    const dealTypeQuery = matchedOfferData?.isFormulaOffer
      ? { deal_type: ["bwi"], $and: { deal_type: "formula" } }
      : { deal_type: ["bwi"] }
    this.dataSource.fetch$
      .pipe(untilComponentDestroyed(this))
      .subscribe(({ filters, page }) => {
        const resetTable = this.nextLoadResetTable
        this.nextLoadResetTable = false
        this.store.dispatch(
          loadDealViews({
            tableKey: this.tableIdentity,
            page,
            filters: {
              ...filters,
              product: [this.data.product],
              status: [ DEAL_CONFIRMED],
              ...dealTypeQuery,
              forceQuery: true,
              convertible: "true",
              ...Object.assign({}, this.data.extendedName || {}),
            },
            calculate: false,
            populate_intransit_balance: false,
            resetTable,
          })
        )
      })

    // reload filters
    trigger$.pipe(untilComponentDestroyed(this)).subscribe((filters) =>
      this.store.dispatch(
        loadDealFilters({
          tableKey: this.tableIdentity,
          filters,
        })
      )
    )

    // listen for columns, sort and pagination changes - apply backend filters
    this.filtersForm.valueChanges
      .pipe(
        distinctUntilChanged((a, b) =>
          isEqual(pick(a, TABLE_FILTERS_FIELDS), pick(b, TABLE_FILTERS_FIELDS))
        ),
        untilComponentDestroyed(this)
      )
      .subscribe(() => this.applyFilters())

      this.actions$.pipe(ofType(
        setOriginLocationSuccess,
        setDestLocationSuccess,
        setSegmentDateSuccess,
      ), untilComponentDestroyed(this)).subscribe(() => {
        this.nextLoadResetTable = true
        this.reloadPage()
      })

    // NOTE: dispatch query action
    this.reloadPage()
  }

  applyFilters() {
    this.flushBackendFilter$.next({ force: false })
  }

  assignToFutureBid() {
    const matchedOfferData = this.data.matchedOffer as MatchedOffer & {isFormulaOffer?: boolean}
    const { offer: { buyers: defaultBuyers } = { buyers: [] }, costs = [] } = matchedOfferData || {};
    const futureDeliveryLocation = matchedOfferData?.bid?.delivery
    this.dealsList.createSupplierOffer(this.selectedDeals[0], { defaultBuyers, costs: costs as Cost[], disableBiddable: false, futureBidId: this.data.future_bid_id, futureBidPrice: Number(this.data.price), futureShipmentDates: this.data.shipmentDates, futureDeliveryDates: this.data.futureDates, futureDeliveryLocation, assignFutureBid: true, minimalAssignMode: true, futureBidIsFormula: !!matchedOfferData?.isFormulaOffer })
      .then((supplierOffer) => {
        if (!supplierOffer) return
        this.ConfirmModal.show({
          title: 'You have successfully assigned an inventory deal to a future bid.',
          titleIcon: 'fa-circle-check',
          description: 'Please choose whether you want to stay on the My Bids page or go to the Pending Matched Offer',
          confirmButtonText: 'GO TO PENDING MO',
          confirmButtonClass: 'btn-primary',
          cancelButtonText: 'STAY ON MY BIDS PAGE',
          cancelButtonClass: 'btn-link',
        }).then(() => {
          this.dialogRef.close(true)
          // Add delay to allow backend indexing to complete
          setTimeout(() => {
            this.router.navigateByUrl(`${ROUTES.TRADING.MATCHED_OFFERS}?supplierOffersIds=${supplierOffer.offer_id}`)
          }, 1500)
        }, () => {
          this.dialogRef.close(true)
        })
      })
  }

  openDeal() {
    if (this.selectedDeals?.length) {
      const { offer: { buyers: defaultBuyers } = { buyers: [] }, costs = [] } = this.data.matchedOffer as MatchedOffer || {};
      this.dealsList.createSupplierOffer(this.selectedDeals[0], { defaultBuyers, costs: costs as Cost[], disableBiddable: true, futureBidId: this.data.future_bid_id, futureBidPrice: Number(this.data.price), futureShipmentDates: this.data.shipmentDates })
    }
  }

  dealIdClick(deal_id: string) {
    this.dealFormPageOverlayService.showDealFormPageOverlay(deal_id)
  }

  dealSelectionChange(deals: DeepReadonly<DealRow[]>) {
    this.selectedDeals = deals
  }



  cancel() {
    this.dialogRef.close()
  }

  private reloadPage() {
    this.flushBackendFilter$.next({ force: true })
  }
}
